import React from 'react';
import {
  Box,
  ActionIcon,
  CloseButton,
  Center,
  Loader,
  Text,
} from '@mantine/core';
import { InlineIcon } from '@iconify/react';
import { useAiCoachAssistantStore } from 'store/aiCoachAssistantStore';
import { useAssistantWidgetStyles } from './AssistantWidget.styles';
import { UserPicker } from './UserPicker';
import { UserList } from './UserList';
import { Chat } from './Chat';

const MemberChatPanel = React.lazy(
  () => import('./MemberChat/MemberChatPanel')
);

export const AssistantWidget = () => {
  const { classes, cx } = useAssistantWidgetStyles();
  const isOpen = useAiCoachAssistantStore((s) => s.isOpen);
  const chatMode = useAiCoachAssistantStore((s) => s.chatMode);
  const displayMode = useAiCoachAssistantStore((s) => s.displayMode);
  const close = useAiCoachAssistantStore((s) => s.close);
  const toggleDisplayMode = useAiCoachAssistantStore(
    (s) => s.toggleDisplayMode
  );
  const sidePaneOpen = useAiCoachAssistantStore((s) => s.widgetSidePaneOpen);
  const toggleSidePane = useAiCoachAssistantStore(
    (s) => s.toggleWidgetSidePane
  );
  const selectedUserId = useAiCoachAssistantStore((s) => s.selectedUserId);

  if (!isOpen || displayMode !== 'widget') return null;

  const isMemberChat = chatMode === 'member';

  const chatBlock = selectedUserId ? (
    <Chat />
  ) : (
    <Box className={classes.placeholder}>
      <Text size="sm" c="dimmed" ta="center">
        Select a user to start chatting
      </Text>
    </Box>
  );

  return (
    <Box
      className={cx(
        classes.container,
        // Member chat shows the channel list beside the conversation, which
        // does not fit the narrow widget.
        (isMemberChat || sidePaneOpen) && classes.containerWide
      )}>
      <Box className={classes.header}>
        <Box className={isMemberChat ? classes.memberTitle : classes.title}>
          <InlineIcon
            icon={isMemberChat ? 'mdi:account' : 'mdi:robot-happy'}
            height={16}
          />
          {isMemberChat ? 'Member Chat' : 'AI Coach Assistant'}
        </Box>
        <Box className={classes.headerActions}>
          {!isMemberChat && (
            <ActionIcon
              size="sm"
              onClick={toggleSidePane}
              title={sidePaneOpen ? 'Hide user list' : 'Show user list'}>
              <InlineIcon
                icon={
                  sidePaneOpen
                    ? 'tabler:layout-sidebar-left-collapse'
                    : 'tabler:layout-sidebar-left-expand'
                }
                height={14}
              />
            </ActionIcon>
          )}
          <ActionIcon size="sm" onClick={toggleDisplayMode} title="Expand">
            <InlineIcon icon="tabler:arrows-maximize" height={14} />
          </ActionIcon>
          <CloseButton size="sm" onClick={close} />
        </Box>
      </Box>

      {isMemberChat ? (
        <React.Suspense
          fallback={
            <Center sx={{ flex: 1 }}>
              <Loader size="sm" />
            </Center>
          }>
          <MemberChatPanel />
        </React.Suspense>
      ) : sidePaneOpen ? (
        <Box className={classes.body}>
          <Box className={classes.leftPanel}>
            <UserList />
          </Box>
          <Box className={classes.rightPanel}>{chatBlock}</Box>
        </Box>
      ) : (
        <>
          <UserPicker />
          <Box className={classes.chatArea}>{chatBlock}</Box>
        </>
      )}
    </Box>
  );
};
